import { AxiosError } from 'axios';
import _ from 'lodash';

export function setCookie(name: string, value: string, days?: number): void {
    let expires = '';
    if (days) {
        const date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        expires = '; expires=' + date.toUTCString();
    }
    document.cookie = name + '=' + (value || '') + expires + '; path=/';
}

export function getCookie(name: string): string | null {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
}
export function eraseCookie(name: string): void {
    document.cookie = name + '=; Max-Age=-99999999;';
}

export function attributeTypeToInputType(attributeType?: string): string | undefined {
    attributeType = attributeType === undefined ? '' : attributeType;
    switch (attributeType.toLowerCase()) {
        case 'int': return 'number';
        case 'float': return 'number';
        case 'string': return 'text';
        case 'date': return 'date';
        default: return 'text';
    }
}

export function filterUnique<T>(value: T, index: number, self: T[]): boolean {
    return self.indexOf(value) === index;
}

export function encodeIdBase64(type: string, Id: string | number): string {
    return btoa(type + '\ni' + Id);
}
export function decodeIdBase64(encoded: string): string {
    return atob(encoded).split('\n')[1].substring(1);
}

// Make sure this is in sync with CodeRefDisplayMode.cs
export function referenceDisplayMode(referenceDisplayMode: number): string {
    switch (referenceDisplayMode) {
        case 0: default:
            return 'Identity';
        case 1:
            return 'Name';
        case 2:
            return 'Description';
        case 3:
            return 'Name and Description';
    }
}

export function levenshtein(s: string, t: string): number {
    const track = Array(t.length + 1).fill(0).map(() =>
        Array(s.length + 1).fill(0));
    for (let i = 0; i <= s.length; i += 1) {
        track[0][i] = i;
    }

    for (let j = 0; j <= t.length; j += 1) {
        track[j][0] = j;
    }

    for (let j = 1; j <= t.length; j += 1) {
        for (let i = 1; i <= s.length; i += 1) {
            const indicator = s[i - 1] === t[j - 1] ? 0 : 1;
            track[j][i] = Math.min(
                track[j][i - 1] + 1, // deletion
                track[j - 1][i] + 1, // insertion
                track[j - 1][i - 1] + indicator // substitution
            );
        }
    }

    return track[t.length][s.length];
}

export function getElementHeight(htmlElement: HTMLElement): number {
    const height = htmlElement?.offsetHeight;
    const styles = window.getComputedStyle(htmlElement);
    return height + parseFloat(styles['marginTop']) + parseFloat(styles['marginBottom']);
}

export function scrollToSelectedTableRow<T>(tableList: T[], value: T, tableRef: HTMLElement): void {
    let index: number | undefined;
    tableList.find((element, i) => {
        if (element && value) {
            if (element === value) {
                index = i;
                return;
            }
        }
    });
    if (index) {
        const row = tableRef.querySelectorAll('tr')[index];
        row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
}

export const codeRefDisplayModes = [
    { value: 0, label: referenceDisplayMode(0) },
    { value: 1, label: referenceDisplayMode(1) },
    { value: 2, label: referenceDisplayMode(2) },
    { value: 3, label: referenceDisplayMode(3) }
];

export const attributeTypes = [
    'String',
    'Int',
    'Float',
    'Bool',
    'Date',
    'DateTime',
    'CodeRef',
    'LibraryRef',
    'Uri',
    'Svg'
];

export function convertToStringAndIsEquals(a: unknown, b: unknown): boolean {
    const aStr = String(a).toLowerCase();
    const bStr = String(b).toLowerCase();
    return aStr === bStr;
}

export type Comparator<T> = (a: T, b: T) => number;

/**
 * Wraps an asynchronous function to maintain only one active operation at a time.
 *
 * When invoked, the wrapper cancels any ongoing asynchronous operation from the last call (if any) and initiates a new
 * one. The supplied asynchronous function, `func`, should be capable of handling an `AbortSignal` for cancellation
 * purposes.
 */
export function cancelPreviousWhenCalled<Args extends unknown[], Result>(
    func: (signal: AbortSignal, ...args: Args) => Promise<Result>
): (...args: Args) => Promise<Result> {
    let abortController: AbortController | null = null;
    return function(...args: Args) {
        abortController?.abort();
        abortController = new AbortController();
        return func(abortController.signal, ...args);
    };
}

/**
 * Wraps an asynchronous operation to both debounce it and maintain only one active operation at a time.
 *
 * When invoked, the wrapper cancels any ongoing asynchronous operation from the last call (if any) and starts a new
 * one immediately.
 */
export function cancelPreviousWhenCalledWithDebouncing<Args extends unknown[]>(
    callback: (abortSignal: AbortSignal, readyPromise: Promise<void>, ...args: Args) => Promise<void> | void,
    { delay, debounceSettings }: { delay?: number; debounceSettings?: _.DebounceSettings } = {}
): (...args: Args) => Promise<void> {
    // Schedules an effect on the "debounce queue" that may or may not be executed.
    const scheduleDebounced = _.debounce((action: () => void) => action(), delay, debounceSettings);

    // Creates a promise that resolves when the operation is ready to proceed.
    function createReadyPromise(abortSignal: AbortSignal): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            scheduleDebounced(() => {
                if (abortSignal.aborted) {
                    reject(abortSignal.reason);
                } else {
                    resolve();
                }
            });
        });
    }

    function rethrowUnlessCancel(ex: unknown): void {
        if (isAbortError(ex) || isCancelAxiosError(ex))
            return;
        throw ex;
    }

    async function wrapper(abortSignal: AbortSignal, ...args: Args): Promise<void> {
        try {
            const readyPromise = createReadyPromise(abortSignal);
            await callback(abortSignal, readyPromise, ...args);
        } catch (e) {
            rethrowUnlessCancel(e);
        }
    }

    return cancelPreviousWhenCalled(wrapper);
}

export function isAbortError(error: unknown): boolean {
    return error instanceof DOMException && error.name === 'AbortError';
}

export function isCancelAxiosError(error: unknown): boolean {
    return error instanceof AxiosError && error.name === 'CanceledError';
}

/**
 * Returns the stepSize to be used with input fields.
 * Default step size is 1.
 * To allow float digits step size, set noDigits to the number of decimals wanted after 0.
 *
 * MDN docs: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/number
 */
export function attributeTypeToStepSize(attributeType?: string, noDigits = 2): string {
    if (attributeType === undefined) return '';
    switch (attributeType.toLowerCase()) {
        case 'int': return '1';
        case 'float': {
            const zero = '0';
            return `0.${zero.repeat(noDigits - 1)}1`;
        }
        default: return '1';
    }
}

/**
 * A locale-aware collator for sorting strings.
 */
const defaultCollator = new Intl.Collator(undefined, {
    usage: 'sort',
    caseFirst: 'upper'
});

export const defaultComparer = (a: unknown, b: unknown): number => {
    if (typeof a === 'string' && typeof b === 'string')
        return defaultCollator.compare(a, b);

    // Sort nullish values last
    if (isNullish(a))
        return isNullish(b) ? 0 : 1;
    if (isNullish(b))
        return -1;

    if (a < b)
        return -1;
    else if (a > b)
        return 1;
    else
        return 0;
};

export function comparing<T, U>(valueSelector: (a: T) => U, valueComparator?: Comparator<U>): Comparator<T> {
    const cmp = valueComparator ?? (defaultComparer as Comparator<U>);
    return (a, b) =>
        cmp(
            valueSelector(a),
            valueSelector(b)
        );
}

export function chainComparers<T>(...comparers: Comparator<T>[]): Comparator<T> {
    return comparers.reduce((acc, next) => (a: T, b: T) => acc(a, b) || next(a, b));
}

export function flipComparer<T>(comparer: Comparator<T>): Comparator<T> {
    return (a, b) => comparer(b, a);
}

/**
 * Returns a function to be used as a compareFn when sorting an array of objects.
 * The objects will be compared by the contents of the property specified by parameter propertyName.
 */
export function compareByProperty<T>(propertyName: keyof T & string): Comparator<T> {
    return comparing((obj: T) => obj[propertyName]);
}

export function createBTableComparator<T>(comparator: Comparator<T>): (a: T, b: T, isAsc: boolean) => number {
    return (a, b, isAsc) => (isAsc ? comparator(a, b) : comparator(b, a));
}

// Collator for checking equality of strings, ignoring case.
const caseInsensitiveEqualityCollator = new Intl.Collator(undefined, {
    // Return value indicates equality only.
    //
    // From the ECMAScript specification:
    // > The collation associated with the "search" value for the "usage" option should only be used to find matching
    // > strings, since this collation is not guaranteed to be in any particular order."
    usage: 'search',
    // Ignore casing, but not accents and other diacritics.
    sensitivity: 'accent'
});

/**
 * Checks if two values are equal with respect to `===`, ignoring case for strings.
 */
export function equalsIgnoreCase(a: unknown, b: unknown): boolean {
    if (typeof a === 'string' && typeof b === 'string') {
        return caseInsensitiveEqualityCollator.compare(a, b) === 0;
    } else {
        return a === b;
    }
}

/**
 * Checks if a path is a prefix of another path.
 */
export function isPrefixOfPath(path: string, prefix: string): boolean {
    const normalizedPath = path.replace(/\/$/, '').toLowerCase();
    const normalizedPrefix = prefix.replace(/\/$/, '').toLowerCase();
    return normalizedPath.startsWith(normalizedPrefix);
}

export function isAbsoluteUrl(url?: string | null): boolean {
    return typeof url === 'string' && /^[a-z][a-z0-9+.-]*:/i.test(url);
}

export function isNullish(obj: unknown): obj is null | undefined {
    return obj === null || obj === undefined;
}
